import { useState } from "react";

import { ComplaintQueuePanel } from "@/components/trust_suite/ComplaintQueuePanel";
import { ResidentDirectoryPanel } from "@/components/trust_suite/ResidentDirectoryPanel";
import { TRUST_SUITE_TABS, type TrustSuiteTab } from "@/components/trust_suite/trustSuiteTabs";
import type { TrustComplaintQueue } from "@/api/trust_complaints";
import { MunicipalitySwitcher } from "@/components/launch_pad/MunicipalitySwitcher";
import { ModuleWorkspaceBody } from "@/components/module_summary/ModuleWorkspaceBody";
import { ModuleWorkspaceHeader } from "@/components/module_summary/ModuleWorkspaceHeader";
import { moduleWorkspaceShellClass } from "@/components/module_summary/moduleWorkspaceLayout";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export function TrustSuiteWorkspace() {
  const [tab, setTab] = useState<TrustSuiteTab>("concerns");
  const [focusComplaint, setFocusComplaint] = useState<{
    id: number;
    queue: TrustComplaintQueue;
  } | null>(null);
  const [focusResidentId, setFocusResidentId] = useState<number | null>(null);

  const handleOpenComplaint = (complaintId: number, queue: TrustComplaintQueue) => {
    setFocusComplaint({id: complaintId, queue});
    setTab("concerns");
  };

  const handleViewResident = (residentId: number) => {
    setFocusResidentId(residentId);
    setTab("residents");
  };

  return (
    <div className={cn(moduleWorkspaceShellClass, "flex-1 min-h-0")} data-testid="trust-suite-workspace">
      <ModuleWorkspaceHeader
        title="Trust Suite"
        subtitle="Work resident concerns and manage resident profiles."
        actions={<MunicipalitySwitcher />}
        meta={
          <div className="flex flex-wrap gap-2 pt-2">
            {TRUST_SUITE_TABS.map((item) => {
              const Icon = item.icon;
              return (
                <Button
                  key={item.id}
                  variant={tab === item.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setTab(item.id)}
                >
                  <Icon className="mr-2 h-4 w-4" />
                  {item.label}
                </Button>
              );
            })}
          </div>
        }
        density="compact"
      />

      <ModuleWorkspaceBody
        className={cn("flex min-h-0 flex-1 flex-col overflow-hidden", "!p-0")}
        contentClassName="flex h-full min-h-0 flex-1 flex-col"
      >
        {tab === "concerns" ? (
          <ComplaintQueuePanel
            focusComplaint={focusComplaint}
            onFocusComplaintConsumed={() => setFocusComplaint(null)}
            onViewResident={handleViewResident}
          />
        ) : null}
        {tab === "residents" ? (
          <ResidentDirectoryPanel
            onOpenComplaint={handleOpenComplaint}
            focusResidentId={focusResidentId}
            onFocusResidentConsumed={() => setFocusResidentId(null)}
          />
        ) : null}
      </ModuleWorkspaceBody>
    </div>
  );
}
